import { useEffect } from 'react';

import type { GQL } from '@aline/common';
import { useTheme } from '@mui/material';
import { drawFramedSigPreview } from 'src/layout-engine/components/dialogs/signature-canvas-utils';

interface UseFramedPreviewOptions {
  canvas: HTMLCanvasElement | null;
  fallbackText: string;
  hPx: number;
  textOrDataUrl: string;
  type: 'text' | 'dataUrl';
  /** Picks the frame's bracket geometry and top label. */
  variant: GQL.SignatureFieldVariant;
  wPx: number;
  /**
   * Skip rendering even when the canvas is mounted. Used by the desktop
   * dialog to keep typed previews stable while the user is on the Draw tab.
   */
  enabled?: boolean;
}

/**
 * Rasterizes the framed "Signed via Aline" preview onto `canvas` whenever the
 * source text/dataUrl or dimensions change. Shared by the desktop adoption
 * dialog and the mobile adoption sheet so adopted signatures look identical
 * regardless of where the user adopts them.
 */
export function useFramedPreview({
  canvas,
  enabled = true,
  fallbackText,
  hPx,
  textOrDataUrl,
  type,
  variant,
  wPx,
}: UseFramedPreviewOptions): void {
  const theme = useTheme();
  const bracketColor = theme.palette.primary.main;

  useEffect(() => {
    if (!canvas || !enabled) return;
    void drawFramedSigPreview(canvas, {
      bracketColor,
      fallbackText,
      hPx,
      previewInfo: { textOrDataUrl, type },
      variant,
      wPx,
    });
  }, [bracketColor, canvas, enabled, fallbackText, hPx, textOrDataUrl, type, variant, wPx]);
}
